import RequirePermissions from '@/components/auth/requirePermissions';
import { AiAssistantButton } from '@/components/GenAI/AiAssistantButton';
import { AiAssistantDrawer } from '@/components/GenAI/AiAssistantDrawer';
import { useArePrecedingMilestonesCompleted } from '@/hooks/useArePrecedingMilestonesCompleted';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import { Alert, Box, useOpenClose } from '@palmetto/palmetto-components';
import { useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { MilestoneType, ProgramType, QueueType, UserPermissions } from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';
import { useGetTasksQuery } from '../../services/tasks';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { taskToMilestoneMap } from '../../types/Tasks';
import { AccountStatusIndicators } from '../Account/AccountStatusIndicators';
import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { SkeletonBox } from '../SkeletonBox';
import AdminStipulationBannerList from '../Stipulations/AdminStipulationBannerList';

import { InstallMilestonePackageFactory } from './InstallMilestonePackageFactory';

export const InstallMilestonePackage = () => {
  const { id, action } = useParams<{ id: any; action: ReviewPackageActions }>();
  const { data: account, isLoading: accountIsLoading } = useGetAccountQuery(id);
  const { data: items = [], isLoading: tasksAreLoading } = useGetTasksQuery({ id });
  const { data: installQueues } = useGetQueuesByAccountIdQuery({ accountId: id, types: [QueueType.installPackage] });
  const installQueue = installQueues?.[0];
  const targetRef = useRef(null);
  const legacyReviewTasks = useMemo(() => {
    return items.filter(
      (item) =>
        item.assigneeType === 'internal' && taskToMilestoneMap[item.type] === MilestoneType.installPackageComplete,
    );
  }, [items]);

  const canSubmitInstallPackage = useArePrecedingMilestonesCompleted(account?.milestones, MilestoneType.install);
  const isGeothermal = account?.programType === ProgramType.geothermal;

  const {
    isOpen: isModelOutputsDrawerOpen,
    handleClose: closeModelOutputsDrawer,
    handleToggle: toggleModelOutputsDrawer,
  } = useOpenClose();
  const parentStyles = useParentDivLayoutStyles(targetRef);
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isModelOutputsDrawerOpen, 'left');

  return (
    <>
      <Helmet>
        <title>{`${account?.primaryApplicantName} Install Package`}</title>
      </Helmet>
      <MainContainer>
        <Box
          ref={targetRef}
          style={{
            marginLeft: drawerMarginStyles,
            transition: 'margin-left 0.3s ease',
          }}
          gap="md"
        >
          <AdminStipulationBannerList />
          <PageHeader
            eyebrow={
              <>
                <Link to="/accounts">Accounts</Link> \{' '}
                <Link to={`/accounts/${id}`}>{`${account?.primaryApplicantName}`}</Link>
              </>
            }
            title={`Install Package${accountIsLoading ? '' : ': ' + account?.primaryApplicantName}`}
            description="Post installation, provide the required documents and install documentation photos for review."
          />
          <AccountStatusIndicators account={account} />
          {account && action !== ReviewPackageActions.review && !canSubmitInstallPackage && (
            <Alert
              variant="warning"
              hasIcon
              title={
                isGeothermal ? 'Loop Field package has not been approved' : 'Notice to Proceed has not been issued'
              }
              message={
                isGeothermal
                  ? 'Please wait for Palmetto approval of the Loop Field milestone before progressing to full Installation.'
                  : 'Notice to Proceed has to be issued before submitting the Install Package.'
              }
            />
          )}
          {accountIsLoading || tasksAreLoading ? (
            <FormSection title="Loading...">
              <SkeletonBox height="25px" width="100" />
            </FormSection>
          ) : (
            <InstallMilestonePackageFactory
              legacyReviewTasks={legacyReviewTasks}
              canSubmitInstallPackage={canSubmitInstallPackage}
            />
          )}
        </Box>
        <RequirePalmettoFinanceUser>
          <RequirePermissions permissions={[UserPermissions.lightReachQCTech]}>
            <AiAssistantButton
              toggleDrawer={toggleModelOutputsDrawer}
              accountId={id}
              isDrawerOpen={isModelOutputsDrawerOpen}
            />
            <AiAssistantDrawer
              accountId={id}
              queueId={installQueue?.id}
              isOpen={isModelOutputsDrawerOpen}
              onClose={closeModelOutputsDrawer}
            />
          </RequirePermissions>
        </RequirePalmettoFinanceUser>
      </MainContainer>
    </>
  );
};
